<template>
  <v-row no-gutters>
    <v-col md="3">
      <text-field
        v-model.trim="search"
        class="search-field"
        outlined
        label="Search Member"
        placeholder="Type member's name or email@"
        maxlength="255"
      />
    </v-col>
    <v-col md="12">
      <data-table
        v-model="selectedRows"
        class="mt-5"
        :search="search"
        :loading="loading"
        :items="members"
        :headers="headers"
        :hide-footer="true"
        :server-items-length="membersMeta.total"
        :page="membersMeta.current_page"
        :custom-columns="['item.time_tracking_status', 'item.member']"
      >
        <template v-slot:custom-render-item.time_tracking_status="{}">
          <div class="d-flex align-center">
            <v-icon
              x-small
              class="dot-icon mr-2"
              :class="`error-dot`"
            >
              fiber_manual_record
            </v-icon>
          </div>
        </template>
        <template v-slot:custom-render-item.member="{ item }">
          <div class="d-flex align-center">
            <div class="custom-member">
              <img
                class="custom-member-img"
                :src="item.image"
                :alt="item.name"
                :title="item.name"
              >
            </div>
            {{ item.name }}
          </div>
        </template>
        <template v-slot:actions-menu="{ item }">
          <data-table-actions>
            <data-table-action-item
              icon="replay"
              @click="callConfirmModal(item)"
            />
          </data-table-actions>
        </template>
        <template v-slot:footer>
          <div v-show="members.length" class="v-data-footer">
            <base-pagination
              v-model="params.page"
              :pagination-meta="membersMeta"
              :has-pagination-pages="false"
              :has-counter-items-on-page="true"
              @change-page="changePaginationPage"
            />
          </div>
        </template>
      </data-table>
    </v-col>
    <table-checked-bar v-model="snackbar">
      <v-row no-gutters>
        <v-col>
          <secondary-button small>
            Move {{ selectedRows.length || '' }}
          </secondary-button>
        </v-col>
        <v-col>
          <secondary-button small>
            Delete {{ selectedRows.length || '' }}
          </secondary-button>
        </v-col>
        <v-col>
          <text-button
            small
            @click="resetSelectedRows"
          >
            Cancel
          </text-button>
        </v-col>
      </v-row>
    </table-checked-bar>
    <confirm-modal
      v-model="isOpenConfirmModal"
      title="Move to active"
      @save="deleteFromInactive"
    >
      Are you sure you want to activate this member?
    </confirm-modal>
  </v-row>
</template>

<script>
import { INACTIVE_MEMBERS } from '@/views/Member/constants';
import TextField from '@/components/ui/TextField/TextField';
import DataTable from '@/components/collections/DataTable/DataTable';
import TableCheckedBar from '@/components/ui/Snackbars/TableCheckedBar';
import SecondaryButton from '@/components/ui/Buttons/SecondaryButton';
import TextButton from '@/components/ui/Buttons/TextButton';
import BasePagination from '@/components/ui/Pagination/BasePagination';
import queryParamsMixin from '@/mixins/queryParamsMixin';
import { debounce, updateParams } from '@/helpers';
import DataTableActionItem from '@/components/collections/DataTableActions/DataTableActionItem';
import DataTableActions from '@/components/collections/DataTableActions/DataTableActions';
import ConfirmModal from '@/components/collections/Dialog/ConfirmModal';
import { apiCall } from '@/services/axios';

export default {
  name: 'Inactive',
  components: {
    TextButton,
    SecondaryButton,
    TableCheckedBar,
    BasePagination,
    DataTableActionItem,
    DataTableActions,
    DataTable,
    TextField,
    ConfirmModal,
  },
  mixins: [queryParamsMixin],
  data() {
    return {
      search: null,
      members: [],
      isOpenConfirmModal: false,
      memberCardId: null,
      headers: INACTIVE_MEMBERS,
      snackbar: false,
      selectedRows: [],
      loading: false,
      params: {
        is_active: '0',
        page: 1,
      },
    };
  },
  computed: {
    selectedWorkspaceId() {
      return this.$store.getters.getSelectedWorkspace.id;
    },
    membersMeta() {
      return this.$store.getters.getMembersMeta;
    },
  },
  watch: {
    selectedRows: {
      deep: true,
      handler(val) {
        this.snackbar = !!val.length;
      },
    },
    search(val) {
      if (val === undefined || val === null) return;

      if (val.length > 255) {
        this.search = val.slice(0, 255);
      }

      this.useDebounce(val);
    },
    params: {
      handler(data) {
        this.setQueryParams({
          search: data.search,
          page: data.page,
        });
      },
      deep: true,
    },
  },
  async mounted() {
    if (Object.keys(this.$route.query).length > 0) {
      this.updateDataByQueryParams(this.$route.query);
      this.search = this.$route.query.search;
    }

    if (!this.$route.query?.search) {
      await this.initialLoad();
    }

    this.$store.subscribe((mutation) => {
      if (mutation.type === 'updateSelectedWorkspace') {
        this.initialLoad();
      }
    });
  },
  created() {
    this.useDebounce = debounce((searchValue) => {
      this.handleSearch(searchValue);
    }, 600);
  },
  methods: {
    handleSearch(searchValue) {
      this.params = updateParams(searchValue, this.params);
      this.initialLoad();
    },
    async initialLoad() {
      this.loading = true;
      await this.$store.dispatch('GET_INACTIVE_MEMBERS', {
        id: this.selectedWorkspaceId,
        params: this.params,
      })
        .then(() => {
          this.members = this.$store.getters.getMembers;
          this.loading = false;
        });
    },
    changePaginationPage(page) {
      this.params.page = page;
      this.loading = true;
      this.$store.dispatch('GET_INACTIVE_MEMBERS', {
        id: this.selectedWorkspaceId,
        params: this.params,
      })
        .then(() => {
          this.members = this.$store.getters.getMembers;
          this.loading = false;
        });
    },
    callConfirmModal(item) {
      this.isOpenConfirmModal = true;
      this.memberCardId = item.member_card_id;
    },
    async deleteFromInactive() {
      try {
        const response = await apiCall('post', `workspaces/${this.selectedWorkspaceId}/member-cards/${this.memberCardId}`, { is_active: true });

        if (response) {
          this.initialLoad();
        }
      } catch (err) {
        console.log(err);
      } finally {
        this.isOpenConfirmModal = false;
        this.memberCardId = null;
      }
    },
    resetSelectedRows() {
      this.selectedRows = [];
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "@/sass/_variables.scss";

  .custom-member {
    display: flex;
    align-items: center;
    &-img {
      width: 28px;
      height: 28px;
      border: 2px solid $white;
      border-radius: 11px;
      margin-right: 8px;
    }
  }
  .search-field {
    ::v-deep .v-input__slot {
      min-width: 280px;
    }
  }

  ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td{
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
